import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import subscriptionRepository from '../services/repositories/subscriptionRepository';
import authService from '../services/auth';
import {
  getPlan,
  checkLimit,
  hasFeature,
  grantsAccess,
  SUBSCRIPTION_STATUS,
  PLAN_ID,
} from '../domain/subscription';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription debe usarse dentro de un SubscriptionProvider');
  return ctx;
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  // Vacío de inicio: el catálogo local sólo lo sirve el repositorio en
  // demostración. Arrancar con PLAN_CATALOG mostraría planes inventados a una
  // cuenta real durante el primer render.
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const load = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [sub, catalogo] = await Promise.all([
        subscriptionRepository.get(),
        subscriptionRepository.listPlans(),
      ]);
      setSubscription(sub);
      setPlans(catalogo);
    } catch (err) {
      console.error('Error cargando la suscripción:', err);
      setError(err.message || 'No se pudo cargar tu suscripción');
      // Sin dato fiable se asume el plan mas restrictivo. Nunca al reves:
      // suponer el plan de pago daria acceso gratis ante cualquier fallo de red.
      setSubscription({ planId: PLAN_ID.FREE, status: SUBSCRIPTION_STATUS.ACTIVE });
    } finally {
      setLoading(false);
    }
  }, []);

  /* Igual que en DeviceContext: el proveedor se monta antes del login, asi que
     comprobar la sesion solo al montar dejaba el plan sin cargar hasta recargar
     la pagina. Se escucha el cambio de sesion en vez de muestrearlo una vez. */
  useEffect(() => {
    load();
    return authService.onSessionChange(() => load());
  }, [load]);

  /**
   * Contrata un plan.
   *
   * SIMULADO: con mocks la suscripcion queda activa al instante.
   * Con backend real devolvera un `checkoutUrl` del proveedor de pagos y habra
   * que redirigir; la confirmacion llegara por webhook, no por esta respuesta.
   */
  const subscribe = useCallback(
    async (planId, opciones = {}) => {
      setProcessing(true);
      setError(null);
      try {
        const { subscription: nueva, checkoutUrl } = await subscriptionRepository.subscribe(
          planId,
          opciones
        );
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
          return { redirected: true };
        }
        setSubscription(nueva);
        return { redirected: false, subscription: nueva };
      } finally {
        setProcessing(false);
      }
    },
    []
  );

  const cancel = useCallback(async () => {
    setProcessing(true);
    try {
      setSubscription(await subscriptionRepository.cancel());
    } finally {
      setProcessing(false);
    }
  }, []);

  const resume = useCallback(async () => {
    setProcessing(true);
    try {
      setSubscription(await subscriptionRepository.resume());
    } finally {
      setProcessing(false);
    }
  }, []);

  const value = useMemo(() => {
    const plan = getPlan(subscription?.planId);
    return {
      subscription,
      plans,
      plan,
      loading,
      error,
      processing,
      isPaid: plan.id !== PLAN_ID.FREE,
      hasAccess: grantsAccess(subscription),
      /**
       * Comprobaciones de limite y funcionalidad.
       * Sirven para ANTICIPAR en la interfaz -deshabilitar un boton, mostrar un
       * aviso-, nunca para autorizar. La autorizacion real es del backend.
       */
      checkLimit: (limite, uso) => checkLimit(subscription, limite, uso),
      hasFeature: (f) => hasFeature(subscription, f),
      subscribe,
      cancel,
      resume,
      reload: load,
    };
  }, [subscription, plans, loading, error, processing, subscribe, cancel, resume, load]);

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};
